import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { functions } from '@/firebaseConfig';
import { createTechnicalMessageId } from './technicalTicketMessages';
import { toDateValue } from './technicalConversation';

const request = httpsCallable(functions, 'sendTechnicalTicketMessage', { timeout: 60000 });
const pendingMessages = new Map();

function retryMetadata(metadata) {
  const copy = { ...metadata };
  delete copy.triggeredAt;
  return copy;
}

function normalizeMetadata(metadata) {
  const copy = { ...metadata };
  for (const key of ['stepIndex', 'programSequenceIndex', 'programSequenceTotal',
    'sequenceIndex', 'creditAmount', 'creditTotal']) {
    if (typeof copy[key] === 'string' && copy[key].trim() && Number.isFinite(Number(copy[key]))) {
      copy[key] = Number(copy[key]);
    }
  }
  for (const key of ['plannedAt', 'creditDate', 'creditExpiryDate']) {
    if (copy[key] && typeof copy[key] === 'object') {
      copy[key] = toDateValue(copy[key])?.toISOString() || null;
    }
  }
  return copy;
}

/** Append an automatic notification with server dates and transaction ordering. */
export async function appendTicketMessage({
  db,
  userUid,
  message,
  title = 'Aggiornamento tecnico',
  ticketId,
  ticketExtra = {},
  conversationExtra = {},
  preserveOpenStatus = true,
  notifyUser: _notifyUser = true,
  awaitNotification: _awaitNotification = true,
  fallbackToLatest = true,
  messageId,
}) {
  if (!db || !userUid || !message) return null;
  void _notifyUser;
  void _awaitNotification;
  const normalizedTicketExtra = normalizeMetadata(ticketExtra);
  const normalizedConversationExtra = normalizeMetadata(conversationExtra);
  const normalizedTitle = String(title || 'Aggiornamento tecnico').slice(0, 200);
  const retryKey = JSON.stringify([
    getAuth().currentUser?.uid, userUid, message, normalizedTitle, ticketId,
    retryMetadata(normalizedTicketExtra), retryMetadata(normalizedConversationExtra), preserveOpenStatus, fallbackToLatest,
  ]);
  const requestId = messageId || pendingMessages.get(retryKey) || createTechnicalMessageId();
  pendingMessages.set(retryKey, requestId);
  try {
    const result = await request({
      operation: 'notification', userUid, message, title: normalizedTitle,
      ...(ticketId ? { ticketId } : {}),
      ticketExtra: normalizedTicketExtra, conversationExtra: normalizedConversationExtra,
      preserveOpenStatus, fallbackToLatest, messageId: requestId,
    });
    if (pendingMessages.get(retryKey) === requestId) pendingMessages.delete(retryKey);
    return result.data;
  } catch (error) {
    if (['functions/permission-denied', 'functions/invalid-argument', 'functions/unauthenticated',
      'functions/already-exists'].includes(error.code) && pendingMessages.get(retryKey) === requestId) {
      pendingMessages.delete(retryKey);
    }
    throw error;
  }
}
